// Frontend LinkedIn Conversions API service
// This tracks page views and other frontend events

interface ConversionEvent {
  eventType: 'page_view' | 'button_click' | 'form_submit'
  page: string
  userId?: string
  userEmail?: string
  additionalData?: Record<string, string>
}

class LinkedInConversionsService {
  private apiUrl: string

  constructor() {
    this.apiUrl = import.meta.env.VITE_API_URL || 'https://api.sourcedin.ai'
  }

  /**
   * Track page view conversion
   */
  async trackPageView(page: string, userId?: string, userEmail?: string) {
    try {
      const event: ConversionEvent = {
        eventType: 'page_view',
        page,
        userId,
        userEmail
      }

      await this.sendEvent(event)
    } catch (error) {
      console.error('Failed to track LinkedIn page view:', error)
    }
  }

  /**
   * Track button click conversion
   */
  async trackButtonClick(buttonName: string, page: string, userId?: string, userEmail?: string) {
    try {
      const event: ConversionEvent = {
        eventType: 'button_click',
        page,
        userId,
        userEmail,
        additionalData: {
          button_name: buttonName
        }
      }

      await this.sendEvent(event)
    } catch (error) {
      console.error('Failed to track LinkedIn button click:', error)
    }
  }

  /**
   * Track form submission conversion
   */
  async trackFormSubmit(formName: string, page: string, userId?: string, userEmail?: string) {
    try {
      const event: ConversionEvent = {
        eventType: 'form_submit',
        page,
        userId,
        userEmail,
        additionalData: {
          form_name: formName
        }
      }

      await this.sendEvent(event)
    } catch (error) {
      console.error('Failed to track LinkedIn form submit:', error)
    }
  }

  /**
   * Send event to backend
   */
  private async sendEvent(event: ConversionEvent) {
    // Only track if we have user data or it's a public page
    if (!event.userId && !event.userEmail && !this.isPublicPage(event.page)) {
      return
    }

    const response = await fetch(`${this.apiUrl}/api/track-conversion`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(event.userId && { 'Authorization': `Bearer ${localStorage.getItem('token')}` })
      },
      body: JSON.stringify(event)
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }
  }

  /**
   * Check if page is public (SEO pages)
   */
  private isPublicPage(page: string): boolean {
    const publicPages = [
      'b2b-marketplace',
      'b2b-leads',
      'b2b-lead-generation',
      'vendor-search',
      'home',
      'about-us',
      'how-it-works',
      'features',
      'pricing',
      'contact'
    ]
    
    return publicPages.some(publicPage => page.includes(publicPage))
  }
}

// Create singleton instance
export const linkedInConversions = new LinkedInConversionsService()

// Hook for React components
export function useLinkedInConversions() {
  return {
    trackPageView: (page: string, userId?: string, userEmail?: string) => 
      linkedInConversions.trackPageView(page, userId, userEmail),
    trackButtonClick: (buttonName: string, page: string, userId?: string, userEmail?: string) => 
      linkedInConversions.trackButtonClick(buttonName, page, userId, userEmail),
    trackFormSubmit: (formName: string, page: string, userId?: string, userEmail?: string) => 
      linkedInConversions.trackFormSubmit(formName, page, userId, userEmail)
  }
}
